import axios from 'axios';
import get from 'lodash/get';

import { processResponse, processError } from './response';

/**
 * Extracts the Rails CSRF token from the HTML shell and returns it in the
 * headers object, to be injected into outbound API requests. If the
 * HTML shell does not provide a CSRF token will return an empty Object.
 */
function csrfHeaders() {
  try {
    const tokenMeta = window.document.querySelector('meta[name="csrf-token"]');
    return {
      'X-CSRF-Token': tokenMeta.content,
    };
  } catch {
    return {};
  }
}

/**
 * Map properties passed by the application to properties
 * expected by axios.
 *
 * @param {Object} options Application request options
 */
export function prepareRequest(options) {
  const method = get(options, 'method', 'get').toLowerCase();
  const isGetRequest = method === 'get';

  // Only include the X-CSRF-Token if the .path (which is in fact
  // not a path but a URL package into a String - any URL) is
  // relative or absolute, but not if it is fully qualified since
  // it leads to an external resource
  const urlIsExternal = /^https?:/.test(options.path);
  const baseHeaders = isGetRequest || urlIsExternal ? {} : csrfHeaders();

  const data = isGetRequest ? null : options.params;
  const params = isGetRequest ? options.params : null;

  return {
    // Complete, absolute or relative URI where we will send the request
    url: options.path,
    // HTTP method. GET if no method is provided
    method,
    // URL parameters to be sent with a GET request, aka querystring params.
    // Must be a plain object or a URLSearchParams object
    params,
    // The data to be sent as the request body.
    // Only applicable for request methods 'PUT', 'POST', and 'PATCH'
    data,
    // Custom headers to send with the request
    headers: {
      ...baseHeaders,
      ...options.headers,
    },
    // Number of milliseconds before the request times out.
    // If the request takes longer than `timeout`, the request will be aborted.
    timeout: get(options, 'timeout', 0),
    callbackParamName: get(options, 'callbackParamName', null),
    // Allows handling of progress events for uploads
    onUploadProgress: get(options, 'onUploadProgress', null),
    // Specifies a cancel token that can be used to cancel the request
    cancelToken: get(options, 'cancelToken', null),
    // Only 2XX and 3XX status code responses are valid
    validateStatus(status) {
      return status >= 200 && status < 400;
    },
  };
}

/**
 * Make a network request using axios. We will map the properties passed by
 * the application to a form expected by axios, and process both success and
 * failed responses to match application expectations.
 *
 * @param {Object} options Properties and data to send in the request
 */
export default function request(options) {
  return axios(prepareRequest(options))
    .then(processResponse)
    .catch(processError);
}
